/**
 * OrgGate — post-sign-in organization selector / creator.
 *
 * Sits between SSOCallback and the app shell. On mount, calls
 * `GET /me/organizations` and decides:
 *
 *   - 0 orgs → show the "create organization" form. User must supply a
 *     name AND a timezone (the timezone gate from the Profile page
 *     applies here too — attendance can't run without it).
 *   - 1 org  → auto-select it + navigate to the return URL (or
 *     /dashboard). No UI flicker for single-org tenants.
 *   - 2+ orgs → show a picker with one card per org, including the role
 *     the user holds in each.
 *
 * Also the redirect target of the axios interceptor when any business
 * endpoint returns 403 "Not a member of this organization" (stale
 * `current_organization_id` in localStorage), so this page doubles as
 * the recovery flow when a user's org context rots.
 *
 * Deliberately not behind PrivateRoute — PrivateRoute's contract is
 * "auth + active org context", and this page exists precisely because
 * org context is missing or invalid. It does require auth (the
 * backend endpoints below require a valid bearer token), but we lean
 * on the platform client's 401 handler to bounce unauth'd users back
 * to /onboarding.
 *
 * Wave 3.1 UX polish: timezone is now a searchable Autocomplete sourced
 * from the shared `constants/enums` registry (400+ IANA names), and
 * currency/language are hard-enum Selects. An optional "Pick HQ on map"
 * button opens the shared LocationPickerDialog to derive timezone +
 * country + sensible currency/language defaults in one click.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import { LOGO as svdyLogo } from '../../branding';

import {
  ApiError,
  organizationApi,
  setCurrentOrgId,
} from '../../api';
import { translateApiError } from '../../i18n/translateApiError';
import type {
  OrganizationCreatePayload,
  OrganizationSummary,
} from '../../api/organization';
import { popReturnTo } from '../../auth/authService';
import {
  filterPortalOrganizations,
  hasPortalRoleCode,
  resolveRoleLanding,
  decideOrgGate,
} from '../../hooks/useRoleLanding';
import { useAuth } from '../../auth/AuthContext';
import { PRODUCT } from '../../branding';
import { roleLabel, roleChipColor } from '../../components/navigation/OrgSwitcher';
import { planChipColor, planChipLabel } from '../../components/billing/planMeta';
import {
  currencies,
  languages,
  timezoneOptions,
  type TimezoneOption,
} from '../../constants/enums';
import {
  LocationPickerDialog,
  type PickedPlace,
} from '../../components/location';

type Mode = 'loading' | 'create' | 'select' | 'submitting';

/**
 * Coarse country → (currency, language) mapping used on the
 * OrgGate create form only. Users can override both in the form
 * immediately. Intentionally small — this is a convenience, not a
 * dataset to maintain.
 */
const COUNTRY_DEFAULTS: Record<string, { currency: string; language: string }> = {
  CN: { currency: 'CNY', language: 'zh-CN' },
  HK: { currency: 'HKD', language: 'zh-TW' },
  TW: { currency: 'TWD', language: 'zh-TW' },
  JP: { currency: 'JPY', language: 'ja' },
  KR: { currency: 'KRW', language: 'ko' },
  SG: { currency: 'SGD', language: 'en' },
  US: { currency: 'USD', language: 'en' },
  CA: { currency: 'CAD', language: 'en' },
  GB: { currency: 'GBP', language: 'en' },
  AU: { currency: 'AUD', language: 'en' },
  IN: { currency: 'INR', language: 'en' },
  DE: { currency: 'EUR', language: 'de' },
  FR: { currency: 'EUR', language: 'fr' },
  IT: { currency: 'EUR', language: 'it' },
  ES: { currency: 'EUR', language: 'es' },
  BR: { currency: 'BRL', language: 'pt-BR' },
  CH: { currency: 'CHF', language: 'de' },
};

const OrgGate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Bug fix 2026-05-22 — sign-out escape from this gate. New users
  // who hit /auth/select-org and decide not to continue had no way
  // to leave; the only path was to manually clear localStorage. Now
  // a top-right "Sign out" button + confirm dialog gives a clean
  // exit. Confirms because the user might have unsaved org-create
  // form input they'd lose.
  const { signOut } = useAuth();
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  const [mode, setMode] = useState<Mode>('loading');
  const [orgs, setOrgs] = useState<OrganizationSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Create-form state
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState<string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [language, setLanguage] = useState<string>('en');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  // Snapshot of the last picked place, so we can hydrate the new org's
  // profile (address / city / state / country / postal_code) in one
  // shot right after create_organization succeeds — saves the user an
  // extra trip to Profile to re-enter the same data.
  const [pickedPlace, setPickedPlace] = useState<PickedPlace | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch user's orgs on mount
  // ---------------------------------------------------------------------------

  const bootstrap = useCallback(async () => {
    setMode('loading');
    setLoadError(null);
    // ?manage=1 → force the picker view even when the user is in a
    // single org. Used by the OrgSwitcher's "Manage organizations"
    // entry, so clicking that doesn't silently bounce back to the app.
    const forceManage =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('manage') === '1';
    try {
      const rows = await organizationApi.listMyOrganizations();
      // eslint-disable-next-line no-console
      console.info(
        '[OrgGate] /me/organizations returned',
        rows.length,
        'org(s)',
        rows.map((r) => r.name),
        'forceManage=',
        forceManage,
      );
      const accessibleRows = filterPortalOrganizations(rows);
      setOrgs(accessibleRows);
      // The branch itself lives in useRoleLanding.decideOrgGate so it can be
      // tested without mounting this page (0-org create form vs. single-org
      // role landing vs. picker).
      const dest = accessibleRows.length === 1 && !forceManage ? popReturnTo() : null;
      const decision = decideOrgGate(rows, { forceManage, intended: dest });
      if (decision.mode === 'create') {
        setMode('create');
      } else if (decision.mode === 'enter') {
        const org = decision.organization as OrganizationSummary;
        setCurrentOrgId(org.id, org.role_code);
        navigate(decision.landing, { replace: true });
      } else {
        // Multi-org OR forced management view → show the picker.
        setMode('select');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OrgGate] /me/organizations failed', e);
      // 2026-05-25 UX rewrite — owner directive: do NOT show a 'something
      // went wrong' interstitial. New users get scared by Retry / contact
      // support copy and bounce.
      //
      // Behavior tree:
      //   401/403 (stale token)        → signOut + redirect to id.svdy.com
      //   anything else (5xx, network) → silent fallthrough to 'create'
      //                                   mode. CreateOrgForm renders a
      //                                   small inline warning banner if
      //                                   loadError is set (so the rare
      //                                   user with existing orgs hitting
      //                                   a backend fault sees: 'we
      //                                   couldn't load your existing orgs
      //                                   but you can still create a new
      //                                   one'). New users see no banner.
      //
      // Tradeoff vs. the 2026-05-22 fix: a user with multi-org history
      // hitting a transient backend fault now sees "Create your org" with
      // a small warning hint instead of a big "Failed to load" page.
      // That's an acceptable rare regression vs. losing every brand-new
      // user to the Retry / contact-support interstitial.
      const isAuthError =
        e instanceof ApiError && (e.status === 401 || e.status === 403);
      if (isAuthError) {
        await signOut();
        return;
      }
      setLoadError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('auth:orgGate.create.loadError'),
      );
      setMode('create');
    }
  }, [navigate, signOut, t]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // ---------------------------------------------------------------------------
  // Select an existing org
  // ---------------------------------------------------------------------------

  const handlePick = (org: OrganizationSummary) => {
    if (!hasPortalRoleCode(org.role_code)) return;
    setCurrentOrgId(org.id, org.role_code);
    const dest = popReturnTo();
    const finalDest = resolveRoleLanding(org.role_code, dest);
    navigate(finalDest, { replace: true });
  };

  // ---------------------------------------------------------------------------
  // LocationPicker → derive timezone + currency/language defaults
  // ---------------------------------------------------------------------------

  const handleLocationPick = useCallback((place: PickedPlace) => {
    // Snapshot for use in handleCreate below — we'll PATCH the new org's
    // profile with street / city / state / country / postal_code on
    // create success, so the user doesn't have to re-enter them in the
    // Profile page afterwards.
    setPickedPlace(place);

    if (place.timezone) {
      setTimezone(place.timezone);
      setFormErrors((prev) => ({ ...prev, timezone: '' }));
    }
    if (place.country && COUNTRY_DEFAULTS[place.country]) {
      const defaults = COUNTRY_DEFAULTS[place.country];
      setCurrency(defaults.currency);
      setLanguage(defaults.language);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Create a new org
  // ---------------------------------------------------------------------------

  const validateCreate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t('auth:orgGate.create.nameRequired');
    if (!timezone) {
      errs.timezone = t('auth:orgGate.create.timezoneRequired');
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateCreate()) return;
    setMode('submitting');
    try {
      // 2026-05-25 Bug 1 fix — fold pickedPlace into the create
      // payload so the backend writes the org row + default Location +
      // creator's primary_location_id in ONE transaction. Pre-fix this
      // was a POST + PATCH chain; the PATCH being slow / 403'd made
      // the create button feel "stuck spinning".
      const payload: OrganizationCreatePayload = {
        name: name.trim(),
        timezone,
        currency: currency || undefined,
        language: language || undefined,
        // 2026-05-29 fix: pass the marketing-site brand (qutime.com vs
        // pureoa.com) so the new tenant's licensed_products matches the
        // entry point. Pre-fix, backend always defaulted to ['qutime']
        // even when the user came from pureoa.com — meaning their
        // dashboard/sidebar showed Attendance instead of Processes.
        // PRODUCT is build-time from REACT_APP_PRODUCT (deploy.yml matrix
        // sets per-bundle).
        licensed_products: [PRODUCT],
        ...(pickedPlace
          ? {
              address: pickedPlace.street_address || undefined,
              city: pickedPlace.city || undefined,
              state: pickedPlace.state || undefined,
              country: pickedPlace.country || undefined,
              postal_code: pickedPlace.postal_code || undefined,
            }
          : {}),
      };
      const created = await organizationApi.createOrganization(payload);

      // The creator is always seeded as super_admin (see
      // svdy-platform organization/service.py::create_organization).
      // Pin role_code here so PrivateRoute doesn't bounce the user
      // back to /auth/select-org on the very next nav (the bounce
      // worked but caused a visible "still on the create page"
      // flash that read as "the button is stuck").
      setCurrentOrgId(created.id, 'super_admin');

      const dest = popReturnTo();
      const finalDest = resolveRoleLanding('super_admin', dest);
      navigate(finalDest, { replace: true });
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('auth:orgGate.create.errorFallback');
      setFormErrors((prev) => ({ ...prev, _global: message }));
      setMode('create');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Bug fix 2026-05-22 — every render below is wrapped in renderShell()
  // which adds the SignOutCorner + confirm dialog. Pre-fix new users
  // who landed here had no escape. Two-click confirm to avoid losing
  // any in-progress org-create form data.
  const renderShell = (content: React.ReactNode) => (
    <>
      <SignOutCorner onConfirmOpen={() => setSignOutConfirmOpen(true)} />
      {content}
      <Dialog
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('auth:orgGate.signOutDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {t('auth:orgGate.signOutDialog.body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setSignOutConfirmOpen(false)}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('auth:orgGate.signOutDialog.stay')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setSignOutConfirmOpen(false);
              signOut();
            }}
            startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {t('auth:orgGate.signOutDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (mode === 'loading') {
    return renderShell(
      <CenteredShell>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {t('auth:orgGate.resolving')}
        </Typography>
      </CenteredShell>,
    );
  }

  // 2026-05-25 — 'error' mode no longer renders a separate page.
  // Bootstrap fail (network / 5xx / non-auth error) falls through to
  // mode='create' with loadError set; CreateOrgForm renders a small
  // inline warning banner. 401/403 redirect via signOut().

  if (mode === 'select') {
    return renderShell(
      <OrgPicker
        orgs={orgs}
        onPick={handlePick}
        onCreateNew={() => setMode('create')}
      />,
    );
  }

  // create + submitting fall through here
  return renderShell(
    <>
      <CreateOrgForm
        name={name}
        onName={setName}
        timezone={timezone}
        onTimezone={setTimezone}
        currency={currency}
        onCurrency={setCurrency}
        language={language}
        onLanguage={setLanguage}
        errors={formErrors}
        submitting={mode === 'submitting'}
        loadError={loadError}
        hasExistingOrgs={orgs.length > 0}
        onBackToPicker={() => setMode('select')}
        onOpenLocationPicker={() => setPickerOpen(true)}
        pickedPlace={pickedPlace}
        onSubmit={handleCreate}
      />
      <LocationPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleLocationPick}
        title={t('auth:orgGate.locationPicker.title')}
      />
    </>,
  );
};

export default OrgGate;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const CenteredShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Container
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      minHeight: '100vh',
    }}
  >
    {children}
  </Container>
);

/**
 * SignOutCorner — top-right escape hatch from /auth/select-org.
 * Bug fix 2026-05-22: pre-fix, a user who reached this gate (post
 * SSO callback) had no way out without manually clearing localStorage.
 * Two-click confirm so misclicks don't drop work-in-progress org
 * create form input.
 */
const SignOutCorner: React.FC<{
  onConfirmOpen: () => void;
}> = ({ onConfirmOpen }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 1100,
    }}
  >
    <Button
      size="small"
      variant="outlined"
      onClick={onConfirmOpen}
      startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
      sx={{
        borderRadius: 1,
        textTransform: 'none',
        fontSize: '0.8125rem',
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {t('auth:orgGate.signOut')}
    </Button>
  </Box>
  );
};

/**
 * BrandHeader — product logo + tagline shared by OrgPicker + CreateOrgForm.
 * Makes it obvious the user is inside the SVDY app and this is a setup
 * step, not a dead-end blank page.
 */
const BrandHeader: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      mb: 3,
    }}
  >
    <Box
      component="img"
      src={svdyLogo}
      alt="SVDY"
      sx={{
        height: 40,
        mb: 1.5,
        userSelect: 'none',
      }}
    />
    {subtitle && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', maxWidth: 420 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const OrgPicker: React.FC<{
  orgs: OrganizationSummary[];
  onPick: (o: OrganizationSummary) => void;
  onCreateNew: () => void;
}> = ({ orgs, onPick, onCreateNew }) => {
  const { t } = useTranslation();
  const sorted = useMemo(
    () =>
      [...orgs].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [orgs],
  );

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <BrandHeader
        subtitle={t('auth:orgGate.picker.subtitle')}
      />
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        {t('auth:orgGate.picker.title')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, textAlign: 'center' }}
      >
        {t('auth:orgGate.picker.memberCount', { count: orgs.length })}
      </Typography>

      <Stack spacing={1.5}>
        {sorted.map((org) => (
          <Card
            key={org.id}
            variant="outlined"
            sx={{ borderRadius: 2, transition: 'border-color 120ms' }}
          >
            <CardActionArea onClick={() => onPick(org)} sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <ApartmentOutlinedIcon
                  sx={{ color: 'primary.main', mt: 0.5 }}
                  fontSize="medium"
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {/* 2026-05-22 polish — visual hierarchy:
                      ┌──────────────────────────────────────┐
                      │ icon  Org Name                  [Plan]│  ← top-right (tier/subscription)
                      │       /slug                            │
                      │       [Role]                           │  ← bottom-left (role badge)
                      └──────────────────────────────────────┘
                      Plan top-right matches industry convention
                      (Apple billing / Stripe / GitHub Pricing) — it's
                      tier information, anchored to corner. Role lives
                      on metadata row, color-coded so different roles
                      are scannable across orgs. */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, minWidth: 0 }}
                      noWrap
                    >
                      {org.name}
                    </Typography>
                    {org.plan && (
                      <Chip
                        label={planChipLabel(org.plan)}
                        size="small"
                        color={planChipColor(org.plan)}
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75 }}
                    noWrap
                  >
                    /{org.slug}
                  </Typography>
                  {org.role_code && (
                    <Chip
                      label={roleLabel(org.role_code)}
                      size="small"
                      color={roleChipColor(org.role_code)}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.72rem' }}
                    />
                  )}
                </Box>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="text"
          startIcon={<AddBusinessOutlinedIcon />}
          onClick={onCreateNew}
          sx={{ textTransform: 'none' }}
        >
          {t('auth:orgGate.picker.createNew')}
        </Button>
      </Box>
    </Container>
  );
};

const CreateOrgForm: React.FC<{
  name: string;
  onName: (v: string) => void;
  timezone: string;
  onTimezone: (v: string) => void;
  currency: string;
  onCurrency: (v: string) => void;
  language: string;
  onLanguage: (v: string) => void;
  errors: Record<string, string>;
  submitting: boolean;
  loadError: string | null;
  hasExistingOrgs: boolean;
  /** Snapshot of the most recent map pick, if any. Used to render a
      preview in the "Pick HQ on a map" card so the user can see which
      address fields will be hydrated on the new org's profile. */
  pickedPlace: PickedPlace | null;
  onBackToPicker: () => void;
  onOpenLocationPicker: () => void;
  onSubmit: (e: React.FormEvent) => void;
}> = ({
  name,
  onName,
  timezone,
  onTimezone,
  currency,
  onCurrency,
  language,
  onLanguage,
  errors,
  submitting,
  loadError,
  hasExistingOrgs,
  pickedPlace,
  onBackToPicker,
  onOpenLocationPicker,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const selectedTz: TimezoneOption | null =
    timezoneOptions.find((tz) => tz.value === timezone) ?? null;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <BrandHeader
        subtitle={
          hasExistingOrgs
            ? t('auth:orgGate.create.subtitleExisting')
            : t('auth:orgGate.create.subtitleNew')
        }
      />
      <Paper
        elevation={0}
        sx={{ p: 4, border: '1px solid #d5d9d9', borderRadius: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('auth:orgGate.create.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth:orgGate.create.description')}
        </Typography>

        {loadError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('auth:orgGate.create.loadWarning', { error: loadError })}
          </Alert>
        )}

        {errors._global && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors._global}
          </Alert>
        )}

        <Box component="form" onSubmit={onSubmit}>
          <TextField
            fullWidth
            label={t('auth:orgGate.create.nameLabel')}
            placeholder={t('auth:orgGate.create.namePlaceholder')}
            value={name}
            onChange={(e) => onName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            autoFocus
            sx={{ mb: 2 }}
          />

          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: pickedPlace ? 'success.main' : 'divider',
              bgcolor: pickedPlace ? 'success.50' : 'grey.50',
            }}
          >
            {pickedPlace ? (
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mb: 0.5 }}
                    >
                      <PlaceOutlinedIcon
                        fontSize="small"
                        color="success"
                        sx={{ fontSize: 16 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'success.main' }}
                      >
                        {t('auth:orgGate.locationPicker.locationPicked')}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: 'text.primary' }}
                      noWrap
                    >
                      {pickedPlace.formatted_address}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    type="button"
                    variant="text"
                    onClick={onOpenLocationPicker}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {t('auth:orgGate.locationPicker.change')}
                  </Button>
                </Stack>

                {/* Field preview — shows exactly what will be saved. */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                    },
                    gap: 0.5,
                    pl: 3,
                  }}
                >
                  {[
                    [t('auth:orgGate.locationPicker.fieldStreet'), pickedPlace.street_address],
                    [t('auth:orgGate.locationPicker.fieldCity'), pickedPlace.city],
                    [t('auth:orgGate.locationPicker.fieldState'), pickedPlace.state],
                    [t('auth:orgGate.locationPicker.fieldCountry'), pickedPlace.country],
                    [t('auth:orgGate.locationPicker.fieldPostalCode'), pickedPlace.postal_code],
                    [t('auth:orgGate.locationPicker.fieldTimezone'), pickedPlace.timezone],
                  ]
                    .filter(([, v]) => !!v)
                    .map(([label, value]) => (
                      <Typography
                        key={label}
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}
                        noWrap
                      >
                        <strong style={{ color: 'inherit', fontWeight: 600 }}>
                          {label}:
                        </strong>{' '}
                        {value}
                      </Typography>
                    ))}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem', lineHeight: 1.35, pl: 3 }}
                >
                  {t('auth:orgGate.locationPicker.savedNote')}
                </Typography>
              </Stack>
            ) : (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                  >
                    {t('auth:orgGate.locationPicker.pickTitle')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25 }}
                  >
                    {t('auth:orgGate.locationPicker.pickDescription')}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  type="button"
                  variant="outlined"
                  startIcon={<PlaceOutlinedIcon fontSize="small" />}
                  onClick={onOpenLocationPicker}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {t('auth:orgGate.locationPicker.openMap')}
                </Button>
              </Stack>
            )}
          </Box>

          <Autocomplete
            options={timezoneOptions as TimezoneOption[]}
            value={selectedTz}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt?.label ?? ''
            }
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            onChange={(_e, v) => onTimezone(v?.value ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('auth:orgGate.create.timezoneLabel')}
                placeholder={t('auth:orgGate.create.timezonePlaceholder')}
                error={!!errors.timezone}
                helperText={
                  errors.timezone ||
                  t('auth:orgGate.create.timezoneHelper')
                }
                required
              />
            )}
            sx={{ mb: 2 }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <FormControl fullWidth sx={{ flex: 1 }}>
              <InputLabel id="cur-label">{t('auth:orgGate.create.currencyLabel')}</InputLabel>
              <Select
                labelId="cur-label"
                label={t('auth:orgGate.create.currencyLabel')}
                value={currency}
                onChange={(e) => onCurrency(e.target.value as string)}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t('auth:orgGate.create.currencyHelper')}
              </FormHelperText>
            </FormControl>
            <FormControl fullWidth sx={{ flex: 1 }}>
              <InputLabel id="lang-label">{t('auth:orgGate.create.languageLabel')}</InputLabel>
              <Select
                labelId="lang-label"
                label={t('auth:orgGate.create.languageLabel')}
                value={language}
                onChange={(e) => onLanguage(e.target.value as string)}
              >
                {languages.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t('auth:orgGate.create.languageHelper')}
              </FormHelperText>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {hasExistingOrgs && (
              <Button
                type="button"
                variant="text"
                onClick={onBackToPicker}
                disabled={submitting}
                sx={{ textTransform: 'none' }}
              >
                {t('auth:orgGate.create.backToPicker')}
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ textTransform: 'none', minWidth: 140 }}
            >
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                t('auth:orgGate.create.submit')
              )}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};
